// src/pages/QuickStartPage.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

type TipProps = { children: React.ReactNode };
function Tip({ children }: TipProps) {
  return <div className="qsTip">💡 {children}</div>;
}

type SectionProps = { title: string; children: React.ReactNode };
function Section({ title, children }: SectionProps) {
  return (
    <div className="qsSection">
      <h4 className="qsSectionTitle">{title}</h4>
      {children}
    </div>
  );
}

export default function QuickStartPage() {
  const { t } = useTranslation();
  const [openStep, setOpenStep] = useState<number | null>(null);

  const toggle = (n: number) => setOpenStep((prev) => (prev === n ? null : n));

  return (
    <div className="container" style={{ maxWidth: 860 }}>
      <div className="pageHeader">
        <div>
          <h1 className="pageTitle">{t("quickStart.title")}</h1>
          <p className="pageNote">{t("quickStart.subtitle")}</p>
        </div>
      </div>

      {/* Hero intro */}
      <div className="card qsHero">
        <div className="qsHeroGrid">
          <div>
            <h2 style={{ marginTop: 0 }}>{t("quickStart.heroTitle")}</h2>
            <p style={{ color: "var(--muted)", marginBottom: 0 }}>{t("quickStart.heroDesc")}</p>
          </div>
          <div className="qsWorkflow">
            <div className="qsWorkflowLabel">{t("quickStart.typicalFlow")}</div>
            {(t("quickStart.workflowSteps", { returnObjects: true }) as string[]).map(
              (step, i) => (
                <div key={i} className="qsWorkflowRow">
                  <span className="qsWorkflowDot">{i + 1}</span>
                  <span>{step}</span>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Collapsible steps */}
      <div className="qsStepList">
        {/* Step 1 */}
        <div className="qsStepCard">
          <button
            type="button"
            className="qsStepToggle"
            onClick={() => toggle(1)}
            aria-expanded={openStep === 1}
          >
            <span className="qsStepToggleLeft">
              <span className="qsStepNumSm">1</span>
              <span className="qsStepIcon" aria-hidden="true">🏢</span>
              <span className="qsStepToggleTitle">{t("quickStart.steps.company.title")}</span>
            </span>
            <span className="qsChevron" aria-hidden="true">{openStep === 1 ? "▲" : "▼"}</span>
          </button>
          {openStep === 1 && (
            <div className="qsStepContent">
              <p>{t("quickStart.steps.company.body")}</p>
              <ul className="qsList">
                {(t("quickStart.steps.company.fields", { returnObjects: true }) as string[]).map(
                  (f, i) => <li key={i}>{f}</li>
                )}
              </ul>
              <Tip>{t("quickStart.steps.company.tip")}</Tip>
            </div>
          )}
        </div>

        {/* Step 2 */}
        <div className="qsStepCard">
          <button type="button" className="qsStepToggle" onClick={() => toggle(2)} aria-expanded={openStep === 2}>
            <span className="qsStepToggleLeft">
              <span className="qsStepNumSm">2</span>
              <span className="qsStepIcon" aria-hidden="true">🧰</span>
              <span className="qsStepToggleTitle">{t("quickStart.steps.resources.title")}</span>
            </span>
            <span className="qsChevron" aria-hidden="true">{openStep === 2 ? "▲" : "▼"}</span>
          </button>
          {openStep === 2 && (
            <div className="qsStepContent">
              <p>{t("quickStart.steps.resources.body")}</p>
              <div className="qsTwoCol">
                <Section title={t("quickStart.steps.resources.workersTitle")}>
                  <ul className="qsList">
                    {(t("quickStart.steps.resources.workersFields", { returnObjects: true }) as string[]).map(
                      (f, i) => <li key={i}>{f}</li>
                    )}
                  </ul>
                </Section>
                <Section title={t("quickStart.steps.resources.subsTitle")}>
                  <ul className="qsList">
                    {(t("quickStart.steps.resources.subsFields", { returnObjects: true }) as string[]).map(
                      (f, i) => <li key={i}>{f}</li>
                    )}
                  </ul>
                </Section>
              </div>
              <Tip>{t("quickStart.steps.resources.tip")}</Tip>
            </div>
          )}
        </div>

        {/* Step 3 */}
        <div className="qsStepCard">
          <button type="button" className="qsStepToggle" onClick={() => toggle(3)} aria-expanded={openStep === 3}>
            <span className="qsStepToggleLeft">
              <span className="qsStepNumSm">3</span>
              <span className="qsStepIcon" aria-hidden="true">👤</span>
              <span className="qsStepToggleTitle">{t("quickStart.steps.clients.title")}</span>
            </span>
            <span className="qsChevron" aria-hidden="true">{openStep === 3 ? "▲" : "▼"}</span>
          </button>
          {openStep === 3 && (
            <div className="qsStepContent">
              <p>{t("quickStart.steps.clients.body")}</p>
              <ul className="qsList">
                {(t("quickStart.steps.clients.fields", { returnObjects: true }) as string[]).map(
                  (f, i) => <li key={i}>{f}</li>
                )}
              </ul>
              <Tip>{t("quickStart.steps.clients.tip")}</Tip>
            </div>
          )}
        </div>

        {/* Step 4 */}
        <div className="qsStepCard">
          <button type="button" className="qsStepToggle" onClick={() => toggle(4)} aria-expanded={openStep === 4}>
            <span className="qsStepToggleLeft">
              <span className="qsStepNumSm">4</span>
              <span className="qsStepIcon" aria-hidden="true">📁</span>
              <span className="qsStepToggleTitle">{t("quickStart.steps.project.title")}</span>
            </span>
            <span className="qsChevron" aria-hidden="true">{openStep === 4 ? "▲" : "▼"}</span>
          </button>
          {openStep === 4 && (
            <div className="qsStepContent">
              <p>{t("quickStart.steps.project.body")}</p>
              <div className="qsTabGrid">
                {(t("quickStart.steps.project.tabs", { returnObjects: true }) as { name: string; icon: string; desc: string }[]).map(
                  (tab, i) => (
                    <div key={i} className="qsTabCard">
                      <div className="qsTabCardHeader">
                        <span aria-hidden="true">{tab.icon}</span>
                        <strong>{tab.name}</strong>
                      </div>
                      <p className="qsTabCardDesc">{tab.desc}</p>
                    </div>
                  )
                )}
              </div>
              <Tip>{t("quickStart.steps.project.tip")}</Tip>
            </div>
          )}
        </div>

        {/* Step 5 */}
        <div className="qsStepCard">
          <button type="button" className="qsStepToggle" onClick={() => toggle(5)} aria-expanded={openStep === 5}>
            <span className="qsStepToggleLeft">
              <span className="qsStepNumSm">5</span>
              <span className="qsStepIcon" aria-hidden="true">🧾</span>
              <span className="qsStepToggleTitle">{t("quickStart.steps.subcontracts.title")}</span>
            </span>
            <span className="qsChevron" aria-hidden="true">{openStep === 5 ? "▲" : "▼"}</span>
          </button>
          {openStep === 5 && (
            <div className="qsStepContent">
              <p>{t("quickStart.steps.subcontracts.body")}</p>
              <ul className="qsList">
                {(t("quickStart.steps.subcontracts.fields", { returnObjects: true }) as string[]).map(
                  (f, i) => <li key={i}>{f}</li>
                )}
              </ul>
              <Tip>{t("quickStart.steps.subcontracts.tip")}</Tip>
            </div>
          )}
        </div>

        {/* Step 6 */}
        <div className="qsStepCard">
          <button type="button" className="qsStepToggle" onClick={() => toggle(6)} aria-expanded={openStep === 6}>
            <span className="qsStepToggleLeft">
              <span className="qsStepNumSm">6</span>
              <span className="qsStepIcon" aria-hidden="true">🕒</span>
              <span className="qsStepToggleTitle">{t("quickStart.steps.wages.title")}</span>
            </span>
            <span className="qsChevron" aria-hidden="true">{openStep === 6 ? "▲" : "▼"}</span>
          </button>
          {openStep === 6 && (
            <div className="qsStepContent">
              <p>{t("quickStart.steps.wages.body")}</p>
              <ol className="qsList">
                {(t("quickStart.steps.wages.steps", { returnObjects: true }) as string[]).map(
                  (s, i) => <li key={i}>{s}</li>
                )}
              </ol>
              <Tip>{t("quickStart.steps.wages.tip")}</Tip>
            </div>
          )}
        </div>

        {/* Step 7 */}
        <div className="qsStepCard">
          <button type="button" className="qsStepToggle" onClick={() => toggle(7)} aria-expanded={openStep === 7}>
            <span className="qsStepToggleLeft">
              <span className="qsStepNumSm">7</span>
              <span className="qsStepIcon" aria-hidden="true">🗓️</span>
              <span className="qsStepToggleTitle">{t("quickStart.steps.schedule.title")}</span>
            </span>
            <span className="qsChevron" aria-hidden="true">{openStep === 7 ? "▲" : "▼"}</span>
          </button>
          {openStep === 7 && (
            <div className="qsStepContent">
              <p>{t("quickStart.steps.schedule.body")}</p>
              <ul className="qsList">
                {(t("quickStart.steps.schedule.features", { returnObjects: true }) as string[]).map(
                  (f, i) => <li key={i}>{f}</li>
                )}
              </ul>
              <Tip>{t("quickStart.steps.schedule.tip")}</Tip>
            </div>
          )}
        </div>

        {/* Step 8 */}
        <div className="qsStepCard">
          <button type="button" className="qsStepToggle" onClick={() => toggle(8)} aria-expanded={openStep === 8}>
            <span className="qsStepToggleLeft">
              <span className="qsStepNumSm">8</span>
              <span className="qsStepIcon" aria-hidden="true">🧾</span>
              <span className="qsStepToggleTitle">{t("quickStart.steps.invoices.title")}</span>
            </span>
            <span className="qsChevron" aria-hidden="true">{openStep === 8 ? "▲" : "▼"}</span>
          </button>
          {openStep === 8 && (
            <div className="qsStepContent">
              <p>{t("quickStart.steps.invoices.body")}</p>
              <ol className="qsList">
                {(t("quickStart.steps.invoices.steps", { returnObjects: true }) as string[]).map(
                  (s, i) => <li key={i}>{s}</li>
                )}
              </ol>
              <Tip>{t("quickStart.steps.invoices.tip")}</Tip>
            </div>
          )}
        </div>

        {/* Step 9 */}
        <div className="qsStepCard">
          <button type="button" className="qsStepToggle" onClick={() => toggle(9)} aria-expanded={openStep === 9}>
            <span className="qsStepToggleLeft">
              <span className="qsStepNumSm">9</span>
              <span className="qsStepIcon" aria-hidden="true">📊</span>
              <span className="qsStepToggleTitle">{t("quickStart.steps.dashboard.title")}</span>
            </span>
            <span className="qsChevron" aria-hidden="true">{openStep === 9 ? "▲" : "▼"}</span>
          </button>
          {openStep === 9 && (
            <div className="qsStepContent">
              <p>{t("quickStart.steps.dashboard.body")}</p>
              <ul className="qsList">
                {(t("quickStart.steps.dashboard.features", { returnObjects: true }) as string[]).map(
                  (f, i) => <li key={i}>{f}</li>
                )}
              </ul>
              <Tip>{t("quickStart.steps.dashboard.tip")}</Tip>
            </div>
          )}
        </div>
      </div>

      {/* Key concepts table */}
      <div className="card" style={{ marginTop: 32 }}>
        <h3 style={{ marginTop: 0 }}>{t("quickStart.concepts.title")}</h3>
        <table className="qsConceptTable">
          <thead>
            <tr>
              <th>{t("quickStart.concepts.colConcept")}</th>
              <th>{t("quickStart.concepts.colMeaning")}</th>
            </tr>
          </thead>
          <tbody>
            {(t("quickStart.concepts.rows", { returnObjects: true }) as { concept: string; meaning: string }[]).map(
              (row, i) => (
                <tr key={i}>
                  <td><strong>{row.concept}</strong></td>
                  <td>{row.meaning}</td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      <div style={{ height: 48 }} />
    </div>
  );
}
